import { useMemo } from "react";
import {
  AlertCircle,
  Calculator,
  ExternalLink,
  Receipt,
  ShieldCheck,
} from "lucide-react";
import { Link } from "@tanstack/react-router";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { PageHeader } from "@/components/layout/PageHeader";
import { useComplianceItems } from "@/hooks/useCompliance";
import { useFamilyProfile } from "@/hooks/useProfile";
import { useTaxProjection } from "@/hooks/useTax";
import { formatDate, formatMoney, formatPercent } from "@/lib/utils";

/**
 * Taxes page now reads from the tax engine, NOT from a marginal-rate slider.
 * If the engine reports blocking missing inputs, we render "—" + a CTA to
 * Tax Intake instead of guessing a number.
 */
export function TaxesPage() {
  const items = useComplianceItems();
  const family = useFamilyProfile();
  const projection = useTaxProjection();

  const fed = useMemo(
    () =>
      (items.data ?? [])
        .filter((c) => c.category === "EstimatedTaxFederal")
        .sort((a, b) => a.dueDate.localeCompare(b.dueDate)),
    [items.data],
  );
  const state = useMemo(
    () =>
      (items.data ?? [])
        .filter((c) => c.category === "EstimatedTaxState")
        .sort((a, b) => a.dueDate.localeCompare(b.dueDate)),
    [items.data],
  );

  const proj = projection.data?.projection;
  const blocked = proj?.hasBlockingGaps ?? false;
  const marginal = proj?.nextDollarBlendedRate ?? null;
  const effective = proj?.effectiveTaxRateOnAgi ?? null;
  const totalLiability = proj?.totalLiability ?? null;
  const remaining = proj?.remainingLiabilityToCover ?? null;
  const llcSlice = projection.data?.marginalBusinessLiability ?? null;

  // Quarterly suggested = remaining-to-cover split fed/state by their projected share.
  const fedShare =
    proj && proj.totalLiability > 0 ? proj.totalFederalTax / proj.totalLiability : 0.83;
  const remainingPerQuarter = remaining != null && remaining > 0 ? remaining / 4 : 0;
  const fedPerQuarter = remainingPerQuarter * fedShare;
  const statePerQuarter = remainingPerQuarter * (1 - fedShare);

  return (
    <>
      <PageHeader
        eyebrow="Taxes"
        title="Estimated payments + projected position"
        description="Federal 1040-ES + Wisconsin Form 1-ES. Numbers come from the tax engine fed by your real intake — no marginal-rate guess."
      />

      {blocked && <BlockedBanner missing={proj?.missingInputs ?? []} />}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <Stat
          label="Total projected liability"
          value={blocked ? "—" : formatMoney(totalLiability)}
          hint={blocked ? "Add intake to compute" : `Effective ${formatPercent(effective)} of AGI`}
          loading={projection.isLoading}
        />
        <Stat
          label="Marginal next-$ rate"
          value={blocked ? "—" : formatPercent(marginal)}
          hint="Federal + WI + SE/Medicare on the next dollar"
          loading={projection.isLoading}
        />
        <Stat
          label="Marginal LLC slice"
          value={blocked || llcSlice == null ? "—" : formatMoney(llcSlice)}
          hint="Tax delta if you removed the business entirely"
          loading={projection.isLoading}
        />
        <Stat
          label="Remaining to cover"
          value={blocked ? "—" : formatMoney(remaining)}
          hint="Liability − YTD W/H − YTD estimates"
          loading={projection.isLoading}
        />
      </div>

      <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="h-4 w-4 text-primary" />
              Federal 1040-ES quarters
            </CardTitle>
            <CardDescription>Pay via EFTPS or IRS Direct Pay; record the confirmation in Compliance.</CardDescription>
          </CardHeader>
          <CardContent>
            <EstimatesTable
              items={fed}
              estimateAmount={fedPerQuarter}
              loading={items.isLoading}
              blocked={blocked}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="h-4 w-4 text-primary" />
              Wisconsin 1-ES quarters
            </CardTitle>
            <CardDescription>Pay via WI DOR EFT; same flow as federal.</CardDescription>
          </CardHeader>
          <CardContent>
            <EstimatesTable
              items={state}
              estimateAmount={statePerQuarter}
              loading={items.isLoading}
              blocked={blocked}
            />
          </CardContent>
        </Card>
      </div>

      {proj && !blocked && <BreakdownCard proj={proj} />}

      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" />
            Safe-harbor posture
          </CardTitle>
          <CardDescription>
            Avoid the underpayment penalty: pay 110% of prior-year tax (AGI &gt; $150k) or 90% of current-year liability.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <Row label="Prior-year AGI" value={formatMoney(family.data?.priorYearAgi ?? null)} />
          <Row label="Prior-year total tax" value={formatMoney(family.data?.priorYearTotalTax ?? null)} />
          <Row
            label="Safe-harbor multiplier"
            value={(family.data?.priorYearAgi ?? 0) > 150_000 ? "110%" : "100%"}
          />
          <Row
            label="Annual safe-harbor target"
            value={formatMoney(
              (family.data?.priorYearTotalTax ?? 0) *
                ((family.data?.priorYearAgi ?? 0) > 150_000 ? 1.1 : 1.0),
            )}
          />
          {(family.data?.priorYearTotalTax ?? 0) === 0 && (
            <p className="text-xs text-muted-foreground">
              No prior-year tax recorded yet — go to{" "}
              <Link to="/tax-intake" className="underline">
                Tax Intake → Prior-year return
              </Link>{" "}
              to enter it.
            </p>
          )}
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-4 w-4 text-primary" />
            Want the S-Corp scenario?
          </CardTitle>
          <CardDescription>
            The Entity Transition Center shows side-by-side total tax across Sole-Prop, S-Corp, S-Corp+FMLLC, and C-Corp.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Link to="/strategy/entity">
            <Button variant="outline">Open Entity Transition Center</Button>
          </Link>
        </CardContent>
      </Card>
    </>
  );
}

function BlockedBanner({
  missing,
}: {
  missing: { fieldKey: string; label: string; why: string; isBlocking: boolean; routeHint: string | null }[];
}) {
  const blockers = missing.filter((m) => m.isBlocking);
  return (
    <Card className="mb-6 border-amber-500/60 bg-amber-500/5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <AlertCircle className="h-4 w-4 text-amber-400" />
          Tax engine cannot project yet — add the missing inputs
        </CardTitle>
        <CardDescription>
          We refuse to invent numbers. Each blocker below maps to a specific intake field.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {blockers.map((m) => (
          <div
            key={m.fieldKey}
            className="flex items-start justify-between gap-3 border-b border-border/40 pb-2 last:border-b-0"
          >
            <div>
              <div className="font-medium">{m.label}</div>
              <div className="text-xs text-muted-foreground">{m.why}</div>
            </div>
            {m.routeHint && (
              <Link to={m.routeHint}>
                <Button size="sm" variant="outline">
                  Open
                </Button>
              </Link>
            )}
          </div>
        ))}
        <div className="pt-2">
          <Link to="/tax-intake">
            <Button size="sm">Open Tax Intake</Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

function BreakdownCard({
  proj,
}: {
  proj: NonNullable<ReturnType<typeof useTaxProjection>["data"]>["projection"];
}) {
  return (
    <Card className="mt-8">
      <CardHeader>
        <CardTitle>How the {formatMoney(proj.totalLiability)} breaks down</CardTitle>
        <CardDescription>
          Year {proj.taxYear} · {humanFiling(proj.filingStatus)} · {proj.stateCode}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Income build-up
          </h3>
          <Row label="W-2 wages" value={formatMoney(proj.totalW2Wages)} />
          <Row label="Net Schedule C" value={formatMoney(proj.netScheduleC)} />
          <Row label="Other income" value={formatMoney(proj.otherIncome)} />
          <Row label="Total income" value={formatMoney(proj.totalIncome)} />
          <Row label="Adjustments (½ SE, retirement, HSA, etc.)" value={formatMoney(-proj.totalAdjustments)} />
          <Row label="AGI" value={formatMoney(proj.agi)} />
          <Row
            label={proj.usedItemized ? "Itemized deduction" : "Standard deduction"}
            value={formatMoney(-proj.standardOrItemizedDeduction)}
          />
          <Row label="QBI deduction" value={formatMoney(-proj.qbiDeduction)} />
          <Row label="Taxable income" value={formatMoney(proj.taxableIncome)} />
        </div>
        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Federal tax
          </h3>
          <Row label="Income tax (brackets)" value={formatMoney(proj.federalIncomeTax.totalTax)} />
          <Row
            label="SE tax (SS + Medicare)"
            value={formatMoney(proj.seTaxTotal)}
            hint={`SS ${formatMoney(proj.seSocialSecurityPortion)} · Med ${formatMoney(proj.seMedicarePortion)}`}
          />
          <Row label="Additional Medicare 0.9%" value={formatMoney(proj.additionalMedicareTax)} />
          <Row label="Net Investment Income Tax" value={formatMoney(proj.netInvestmentIncomeTax)} />
          <Row label="Total federal" value={formatMoney(proj.totalFederalTax)} />

          <h3 className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Wisconsin
          </h3>
          <Row label="WI taxable income" value={formatMoney(proj.wisconsinTaxableIncome)} />
          <Row label="WI income tax" value={formatMoney(proj.wisconsinIncomeTax.totalTax)} />
          <Row label="Total state" value={formatMoney(proj.totalStateTax)} />

          <h3 className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Settle-up
          </h3>
          <Row label="Total liability" value={formatMoney(proj.totalLiability)} />
          <Row label="YTD withholding" value={formatMoney(-proj.totalWithholdingYtd)} />
          <Row label="YTD estimates paid" value={formatMoney(-proj.totalEstimatedPaidYtd)} />
          <Row label="Remaining to cover" value={formatMoney(proj.remainingLiabilityToCover)} />
        </div>
        {proj.warnings.length > 0 && (
          <div className="lg:col-span-2 mt-2 rounded-md border border-border/60 bg-muted/30 p-3 text-xs text-muted-foreground">
            <div className="mb-1 font-semibold text-foreground">Warnings (non-blocking):</div>
            <ul className="list-disc space-y-0.5 pl-5">
              {proj.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function humanFiling(s: string) {
  switch (s) {
    case "MarriedFilingJointly":
      return "MFJ";
    case "MarriedFilingSeparately":
      return "MFS";
    case "HeadOfHousehold":
      return "HoH";
    case "QualifyingSurvivingSpouse":
      return "QSS";
    default:
      return "Single";
  }
}

function EstimatesTable({
  items,
  estimateAmount,
  loading,
  blocked,
}: {
  items: Array<{
    id: string;
    title: string;
    dueDate: string;
    status: string;
    authorityUrl?: string | null;
    confirmationNumber?: string | null;
  }>;
  estimateAmount: number;
  loading: boolean;
  blocked: boolean;
}) {
  if (loading) return <Skeleton className="h-32 w-full" />;
  if (items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No quarters seeded yet for this category.</p>
    );
  }
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-border text-left text-muted-foreground">
          <th className="py-2">Quarter</th>
          <th className="py-2">Due</th>
          <th className="py-2 text-right">Suggested</th>
          <th className="py-2">Status</th>
          <th className="py-2"></th>
        </tr>
      </thead>
      <tbody>
        {items.map((c) => {
          const m = c.title.match(/Q(\d)/);
          return (
            <tr key={c.id} className="border-b border-border/50">
              <td className="py-2 font-medium">{m ? `Q${m[1]}` : c.title}</td>
              <td className="py-2 text-muted-foreground">{formatDate(c.dueDate)}</td>
              <td className="py-2 text-right">
                {blocked ? "—" : formatMoney(estimateAmount)}
              </td>
              <td className="py-2">
                <Badge
                  tone={c.status === "Completed" ? "success" : c.status === "Overdue" ? "danger" : "warning"}
                >
                  {c.status === "Completed" ? "Paid" : c.status === "Overdue" ? "Overdue" : "Upcoming"}
                </Badge>
              </td>
              <td className="py-2 text-right">
                {c.authorityUrl && (
                  <a
                    href={c.authorityUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                  >
                    Pay <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function Stat({
  label,
  value,
  hint,
  loading,
}: {
  label: string;
  value: string;
  hint?: string;
  loading?: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? <Skeleton className="h-8 w-24" /> : <div className="text-2xl font-semibold">{value}</div>}
        {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
      </CardContent>
    </Card>
  );
}

function Row({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex items-center justify-between border-b border-border/40 py-1.5 last:border-b-0">
      <span className="text-muted-foreground">
        {label}
        {hint && <span className="ml-1 text-xs">({hint})</span>}
      </span>
      <span className="font-medium">{value}</span>
    </div>
  );
}
